import React, { useState, useEffect } from "react";
import axios from "axios";
import "./BookingForm.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  faBuilding,
  faUser,
  faCalendarAlt,
  faDollarSign,
  faEnvelope,
  faGlobe,
  faPassport,
  faPhone,
  faUsers,
  faChild
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const BookingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const unitFromState = location.state?.unitname || "";
  const priceFromState = location.state?.price || "";

  const cleanPrice = (priceStr) => {
    if (!priceStr) return "";
    const match = priceStr.toString().match(/\d+/);
    return match ? match[0] : "";
  };

  useEffect(() => {
    if (!unitFromState) {
      alert("Please select an apartment unit first.");
      navigate("/");
    }
  }, [unitFromState, navigate]);

  const [formData, setFormData] = useState({
    guest_name: "",
    arrival_date: "",
    departure_date: "",
    appartment_unit: unitFromState,
    rent: cleanPrice(priceFromState),
    email_address: "",
    nationality: "",
    country_residence: "",
    passport_id: "",
    mobile_number: "",
    number_adults: "",
    number_children: ""
  });

  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    if (e.target.name === "rent") {
      setErrorMessage("");
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const currentUnit = formData.appartment_unit;
    const inputRent = parseFloat(formData.rent);

    const tier180 = [
      "Akeza-A1", "Gwiza-A2", "Heza-A3",
      "Beza-B1", "Cyiza-B2", "Ineza-B3"
    ];

    const tier120 = [
      "Keza-C1", "Neza-C2", "Gira-C3",
      "Ikaze-D1", "Gwiza-D2", "Ubuntu-D3"
    ];

    if (tier180.includes(currentUnit) && inputRent < 180) {
      setErrorMessage(`Minimum booking amount for "${currentUnit}" is $180.`);
      return;
    }

    if (tier120.includes(currentUnit) && inputRent < 120) {
      setErrorMessage(`Minimum booking amount for "${currentUnit}" is $120.`);
      return;
    }

    try {
      await axios.post(
        "https://ikaze-thouse-backend.vercel.app/api/v1/bookings/create",
        formData
      );

      alert("Booking Submitted Successfully");
      navigate("/");

    } catch (error) {
      console.error(error);
      alert("Failed to submit booking");
    }
  };

  return (
    <div className="bk-page-wrapper">
      <div className="bk-container">
        <form className="bk-form" onSubmit={handleSubmit}>

          <div className="bk-header">
            <h2>Apartment Booking</h2>
            <p>Complete the form below to secure your apartment reservation.</p>
          </div>

          {errorMessage && (
            <div className="bk-validation-error-alert">
              ⚠️ {errorMessage}
            </div>
          )}

          <div className="bk-grid">

            {/* UNIT LOCKED */}
            <div className="bk-group bk-full-width">
              <label>
                <FontAwesomeIcon icon={faBuilding} /> Selected Apartment Unit
              </label>
              <input
                type="text"
                name="appartment_unit"
                value={formData.appartment_unit}
                readOnly
                className="bk-readonly-field"
              />
            </div>

            {/* GUEST NAME */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faUser} /> Guest Name *</label>
              <input
                type="text"
                name="guest_name"
                value={formData.guest_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* EMAIL */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faEnvelope} /> Email *</label>
              <input
                type="email"
                name="email_address"
                value={formData.email_address}
                onChange={handleChange}
                required
              />
            </div>

            {/* DATES */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faCalendarAlt} /> Check-In</label>
              <input
                type="date"
                name="arrival_date"
                value={formData.arrival_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="bk-group">
              <label><FontAwesomeIcon icon={faCalendarAlt} /> Check-Out</label>
              <input
                type="date"
                name="departure_date"
                value={formData.departure_date}
                onChange={handleChange}
                required
              />
            </div>

            {/* RENT */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faDollarSign} /> Amount *</label>
              <input
                type="number"
                name="rent"
                value={formData.rent}
                onChange={handleChange}
                required
              />
            </div>

            {/* PHONE */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faPhone} /> Phone</label>
              <input
                type="text"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
              />
            </div>

            {/* NATIONALITY */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faGlobe} /> Nationality</label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
              />
            </div>

            {/* COUNTRY */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faGlobe} /> Country</label>
              <input
                type="text"
                name="country_residence"
                value={formData.country_residence}
                onChange={handleChange}
              />
            </div>

            {/* PASSPORT */}
            <div className="bk-group bk-full-width">
              <label><FontAwesomeIcon icon={faPassport} /> Passport ID</label>
              <input
                type="text"
                name="passport_id"
                value={formData.passport_id}
                onChange={handleChange}
              />
            </div>

            {/* ADULTS */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faUsers} /> Adults</label>
              <input
                type="number"
                name="number_adults"
                value={formData.number_adults}
                onChange={handleChange}
              />
            </div>

            {/* CHILDREN */}
            <div className="bk-group">
              <label><FontAwesomeIcon icon={faChild} /> Children</label>
              <input
                type="number"
                name="number_children"
                value={formData.number_children}
                onChange={handleChange}
              />
            </div>

          </div>

          <button type="submit" className="bk-submit-btn">
            Confirm Booking
          </button>

        </form>
      </div>
    </div>
  );
};

export default BookingForm;